import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import Teachr from "../../Objects/models/Teachr";
import DialogLayout from "../../Components/DialogLayout";
import { isDepositEnabled } from "../../config/environment.config";
import { MAX_SESSIONS_MONTH, SessionRow, nextSessionRow, sortSessionRows } from "../DeclarationCourse/SessionList";
import { DV2 } from "../DeclarationV2/theme";
import { euros } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import WizardShell from "../DeclarationV2/layout/WizardShell";
import { Button, Card, FieldError, FieldLabel, Hint, Notice, SectionTitle, TextLink, fieldStyle, useDv2Theme } from "../DeclarationV2/layout/Ui";
import SessionCard from "../DeclarationV2/components/SessionCard";
import DepositPanel from "../DeclarationV2/components/DepositPanel";
import SessionDescriptionPanel from "../DeclarationV2/components/SessionDescriptionPanel";
import { AmountsBlock, scopeLabel } from "../DeclarationV2/components/AmountsBlock";
import { computeAmounts } from "../DeclarationV2/components/amounts";
import { relationRemuneration } from "../DeclarationV2/components/relationTeachr";
import { RelationRateField, RelationRemunerationBlock } from "../DeclarationV2/components/RelationRemunerationBlock";
import { enforceEarliestStart, firstSelectableDay } from "../DeclarationV2/components/planningRules";
import { Draft, DraftEdits, draftWithEdits, editsFromDraft } from "./types";
import { draftHours, draftIssues, draftIssuesSummary, hasDraftIssues } from "./drafts";
import useDeclareDraft, { DraftRefusal } from "./useDeclareDraft";
import DraftDaysPicker from "./DraftDaysPicker";
import { courseDatesLabel } from "../CoursesV2/courseRows";
import { draftsPath } from "./paths";

interface Props {
    draft: Draft;
    teachr: Teachr;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Modifier un brouillon (maquettes 1h et 2h), en page et non en modale : une
 * carte par séance avec l'erreur sur la carte fautive, prix horaire payé et
 * « Vous recevez », acompte à certifier de nouveau, déroulement propre au
 * cours, résumé des erreurs près des boutons avec « Y aller ». « Déclarer »
 * reprend les contrôles et la chaîne d'envoi de l'onglet existant ; un refus
 * venu de la liste arrive ici avec ses erreurs affichées. Quitter avec des
 * changements demande confirmation (rien n'est enregistré sans déclaration).
 */
const DraftEdit = ({ draft, teachr }: Props) => {
    const { fontFamily } = useDv2Theme();
    const navigate = useNavigate();
    const location = useLocation();
    const arrival = (location.state as { fromDeclare?: boolean; overlap?: DraftRefusal["overlap"] } | null) ?? null;

    const [edits, setEdits] = useState<DraftEdits>(() => editsFromDraft(draft));
    const [showIssues, setShowIssues] = useState(!!arrival?.fromDeclare);
    const [overlap, setOverlap] = useState<DraftRefusal["overlap"] | null>(arrival?.overlap ?? null);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [genericDescription, setGenericDescription] = useState<string | null>(null);

    const current = useMemo(() => draftWithEdits(draft, edits), [draft, edits]);
    const dirty = useMemo(() => JSON.stringify(edits) !== JSON.stringify(editsFromDraft(draft)), [edits, draft]);
    const issues = useMemo(() => draftIssues(current, teachr), [current, teachr]);
    const hours = draftHours(current);
    const amounts = computeAmounts(current.price, hours, teachr, { zip: draft.student.zip }, current.deposit.enabled ? current.deposit.amount : 0);
    // relation Teach'r : tarif fixé par la plateforme, seule la rémunération du prof est montrée
    const remuneration = relationRemuneration(draft.raw, hours);
    const depositAllowed = draft.isTeachrContact && isDepositEnabled() && !(teachr as any)?.scoop;
    const minDate = firstSelectableDay(draft.student.activationDate);
    const rows = sortSessionRows(current.sessions);

    // Une séance modifiée n'est plus celle du chevauchement signalé par la liste.
    const overlapRow = overlap ? rows[overlap.index - 1] : null;
    const issueByKey = new Map<string, string>(issues.rows.map((i) => [i.key, i.message]));
    if (overlapRow && !issueByKey.has(overlapRow.key)) issueByKey.set(overlapRow.key, overlap!.message);

    const { declare, dialogs, busy } = useDeclareDraft(teachr, {
        onRefused: (_draft: Draft, refusal: DraftRefusal) => {
            setShowIssues(true);
            setOverlap(refusal.overlap ?? null);
            const first = issues.rows[0]?.key ?? (refusal.overlap ? rows[refusal.overlap.index - 1]?.key : null);
            if (first) goToRow(first);
        },
        onDeclared: () => navigate(draftsPath(), { replace: true }),
    });

    useEffect(() => {
        if (arrival?.fromDeclare && issues.rows[0]) {
            setExpandedKey(issues.rows[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goToRow = (key: string) => {
        setExpandedKey(key);
        document.getElementById(`dv2-session-${key}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const setRows = (next: SessionRow[]) => {
        setOverlap(null);
        setEdits((e) => ({ ...e, sessions: enforceEarliestStart(next, draft.student.activationDate) }));
    };
    const changeRow = (key: string, patch: Partial<SessionRow>) => setRows(current.sessions.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    const removeRow = (key: string) => setRows(current.sessions.filter((row) => row.key !== key));
    const addRow = () => {
        if (current.sessions.length >= MAX_SESSIONS_MONTH) return;
        const row = nextSessionRow(current.sessions);
        setRows([...current.sessions, row]);
        setExpandedKey(row.key);
    };
    const canRemove = draft.multiple && current.sessions.length > draft.minSessions;
    // Ancien cours sans détail des séances : calendrier tant qu'aucun jour n'est choisi.
    const pickingDays = draft.multiple && draft.needsDates && current.sessions.length === 0;

    const leave = () => navigate(draftsPath());
    const back = () => (dirty ? setLeaveOpen(true) : leave());

    const summary = showIssues ? draftIssuesSummary(current, issues) ?? overlap?.message ?? null : null;
    const firstIssueKey = issues.rows[0]?.key ?? overlapRow?.key ?? null;
    const subtitle = `${draft.student.firstName} ${draft.student.lastName}${draft.subjectLabel ? `, ${draft.subjectLabel.toLowerCase()}` : ""}, ${draft.multiple ? vocab.multipleCoursesTitle().toLowerCase() : vocab.singleCourseTitle().toLowerCase()}`;

    return (
        <>
            {dialogs}
            <DialogLayout
                title="Quitter sans déclarer ?"
                text={
                    <p style={{ fontFamily, fontSize: 15, lineHeight: 1.55, color: DV2.text, margin: 0 }}>
                        Vos modifications ne sont pas conservées tant que le brouillon n'est pas déclaré. Si vous quittez maintenant, le brouillon reprendra les informations {vocab.ofTheCourse()} du mois dernier.
                    </p>
                }
                valid="Quitter"
                cancel="Rester"
                open={leaveOpen}
                handleSubmit={leave}
                handleClose={() => setLeaveOpen(false)}
            />
            <WizardShell
                title="Modifier le brouillon"
                subtitle={subtitle}
                onBack={back}
                narrow
                footer={{
                    onPrevious: back,
                    previousLabel: "Retour à la déclaration rapide",
                    onNext: () => { setShowIssues(true); declare(current); },
                    nextLabel: pickingDays ? "Choisissez d'abord les jours" : draft.multiple ? `Déclarer les ${rows.length} séances` : `Déclarer ${vocab.theCourse()}`,
                    nextLoading: busy,
                    nextDisabled: busy || pickingDays,
                    extra: summary ? (
                        <Notice tone="danger" id="bv2-summary" action={firstIssueKey ? <TextLink label="Y aller" onClick={() => goToRow(firstIssueKey)} /> : undefined}>{summary}</Notice>
                    ) : dirty ? (
                        <span style={{ fontSize: 13, color: DV2.warning, fontWeight: 700 }}>Modifications en attente</span>
                    ) : undefined,
                }}
            >
                {pickingDays ? (
                    <DraftDaysPicker
                        activationDate={draft.student.activationDate}
                        initialTime={draft.previousStartTime}
                        previousPeriod={courseDatesLabel(draft.raw)}
                        previousHours={(Number(draft.raw?.duration ?? 0) || 0) / 2}
                        onGenerate={(generated) => setRows(generated)}
                    />
                ) : (
                <div className="flex flex-wrap gap-2 justify-between items-center">
                    <div style={{ fontSize: 13, color: DV2.muted }}>
                        {draft.multiple
                            ? draft.needsDates
                                ? `Vos séances de ${moment().locale("fr").format("MMMM")}, générées depuis les jours cochés. Touchez une séance ou son crayon pour l'ajuster.`
                                : `Vos séances de ${moment().locale("fr").format("MMMM")}, reprises du mois dernier avec le même jour de semaine. Touchez une séance ou son crayon pour l'ajuster.`
                            : `Votre ${vocab.course()} repris du mois dernier, un mois plus tard. Touchez-le ou son crayon pour l'ajuster.`}
                    </div>
                    {draft.multiple && (
                        <div className="flex flex-wrap gap-3 items-center">
                            {draft.needsDates && <TextLink label="Rechoisir les jours" onClick={() => setRows([])} />}
                            <Button label="+ Ajouter une séance" variant="secondary" size="sm" onClick={addRow} disabled={current.sessions.length >= MAX_SESSIONS_MONTH} />
                        </div>
                    )}
                </div>
                )}

                <div className="flex flex-col gap-3">
                    {rows.map((row, index) => (
                        <SessionCard
                            key={row.key}
                            row={row}
                            index={index}
                            issue={showIssues || overlapRow?.key === row.key ? issueByKey.get(row.key) : undefined}
                            expanded={expandedKey === row.key}
                            onToggle={() => setExpandedKey(expandedKey === row.key ? null : row.key)}
                            onChange={(patch) => changeRow(row.key, patch)}
                            onRemove={canRemove ? () => removeRow(row.key) : undefined}
                            minDate={minDate}
                        />
                    ))}
                    {showIssues && issues.sessions && <Notice tone="danger">{issues.sessions}</Notice>}
                    {showIssues && issues.quota && <Notice tone="danger">{issues.quota}</Notice>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <Card style={{ display: "flex", flexDirection: "column", gap: 12, padding: 22 }}>
                        <SectionTitle>{draft.isRelationTeachr ? "Votre tarif" : "Prix de l'heure"}</SectionTitle>
                        {draft.isRelationTeachr ? (
                            <RelationRateField remuneration={remuneration} firstName={draft.student.firstName} error={showIssues ? issues.price : null} />
                        ) : (
                        <div>
                            <FieldLabel htmlFor="bv2-price" hint={`par ${vocab.yourClient()}`}>Prix horaire payé</FieldLabel>
                            <div style={{ position: "relative" }}>
                                <input
                                    id="bv2-price"
                                    type="number"
                                    inputMode="decimal"
                                    min={6}
                                    max={138}
                                    step="0.5"
                                    value={current.price}
                                    disabled={!draft.isTeachrContact}
                                    onChange={(e) => setEdits((ed) => ({ ...ed, price: e.target.value }))}
                                    aria-invalid={showIssues && !!issues.price}
                                    aria-describedby={showIssues && issues.price ? "bv2-price-error" : undefined}
                                    style={{ ...fieldStyle(showIssues && !!issues.price), fontFamily, fontSize: 20, fontWeight: 800, paddingRight: 110 }}
                                />
                                <span aria-hidden style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: DV2.muted }}>€ par heure</span>
                            </div>
                            {showIssues && issues.price ? <FieldError id="bv2-price-error">{issues.price}</FieldError> : (
                                <Hint>
                                    C'est ce que paie {vocab.yourClient()} pour une heure, avance immédiate déduite, pas ce que vous recevez.{" "}
                                    {!draft.isTeachrContact ? `Tarif fixé avec ${draft.student.firstName} dans sa fiche.` : `Même tarif que le mois dernier : ${euros(draft.price)}.`}
                                </Hint>
                            )}
                        </div>
                        )}
                        {showIssues && issues.description && <Notice tone="danger">{issues.description}</Notice>}
                    </Card>
                    {draft.isRelationTeachr
                        ? <RelationRemunerationBlock remuneration={remuneration} scope={scopeLabel(draft.multiple ? rows.length : 1)} />
                        : <AmountsBlock amounts={amounts} scope={scopeLabel(draft.multiple ? rows.length : 1)} showDeposit={depositAllowed} />}
                </div>

                {depositAllowed && (
                    <DepositPanel
                        teachr={teachr}
                        child={{ clientId: draft.student.clientId, childFirstName: draft.student.firstName, childLastName: draft.student.lastName, zip: draft.student.zip }}
                        value={current.deposit}
                        onChange={(deposit) => setEdits((e) => ({ ...e, deposit }))}
                        ceiling={hours * (parseFloat(current.price) || 0)}
                        minDate={draft.student.activationDate ? moment(draft.student.activationDate).format("YYYY-MM-DD") : null}
                        errors={showIssues ? issues.deposit : {}}
                        multiple={draft.multiple}
                    />
                )}

                <SessionDescriptionPanel
                    teachr={teachr}
                    value={current.sessionDescriptionOverride}
                    onChange={(value) => setEdits((e) => ({ ...e, sessionDescriptionOverride: value }))}
                    onGenericLoaded={setGenericDescription}
                    error={showIssues ? issues.description ?? undefined : undefined}
                />
                {genericDescription === null && null}

                {showIssues && hasDraftIssues(issues) && issues.all.length > 1 && (
                    <Card tint style={{ padding: "14px 18px" }}>
                        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>{capitalize(`${issues.all.length} points à corriger`)}</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: DV2.textSoft, lineHeight: 1.5 }}>
                            {issues.all.map((message) => <li key={message}>{message}</li>)}
                        </ul>
                    </Card>
                )}
            </WizardShell>
        </>
    );
};

export default DraftEdit;
